import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../design-system';
import { MAX_TAGS_PER_PERSON, parseTags, type DirectoryUser } from '../../lib/adminUsers';

export type BulkMode = 'add_tag' | 'remove_tag' | 'push';

/** One action for every person ticked in the Users list. Says how many people before anything is sent. */
export function BulkDialog({ mode, people, onClose, onDone }: {
  mode: BulkMode;
  people: DirectoryUser[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [tagText, setTagText] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = people.length;
  const who = `${n} ${n === 1 ? 'person' : 'people'}`;
  const parsed = parseTags(tagText);
  const tagReady = parsed.tags.length > 0 && parsed.invalid.length === 0;
  const pushReady = title.trim().length > 0 && message.trim().length > 0;

  async function run() {
    setWorking(true);
    setError(null);
    const ids = people.map((p) => p.id);
    if (mode === 'push') {
      const { data, error: err } = await supabase.functions.invoke('broadcast-push', {
        body: { title: title.trim(), body: message.trim(), audience: 'selected', user_ids: ids },
      });
      setWorking(false);
      if (err) {
        let reason: string | undefined;
        try { reason = ((await (err as { context?: Response }).context?.json()) as { error?: string })?.error; } catch { /* keep the default */ }
        setError(reason || "The message couldn't be sent. Try again in a moment.");
        return;
      }
      const r = data as { sent?: number; failed?: number; total_tokens?: number } | null;
      onDone(r && r.total_tokens === 0
        ? 'None of them has a phone registered for push, so nothing was sent.'
        : `Sent to ${r?.sent ?? 0} device${r?.sent === 1 ? '' : 's'}${r?.failed ? `, ${r.failed} failed` : ''}.`);
      return;
    }
    const { data, error: err } = await supabase.rpc('admin_set_user_tags', {
      p_user_ids: ids,
      p_add: mode === 'add_tag' ? parsed.tags : [],
      p_remove: mode === 'remove_tag' ? parsed.tags : [],
    });
    setWorking(false);
    if (err) {
      setError(err.message || "We couldn't change the tags.");
      return;
    }
    const changed = (data as number | null) ?? 0;
    onDone(changed === 0 ? 'Nothing changed: they already matched.' : `Updated tags for ${changed} ${changed === 1 ? 'person' : 'people'}.`);
  }

  const heading = mode === 'push' ? 'Send a push message' : mode === 'add_tag' ? 'Add tags' : 'Remove tags';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={working ? undefined : onClose}>
      <div role="dialog" aria-label={heading} className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-md p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h2 className="text-lg font-semibold text-app-fg">{heading}</h2>
          <p className="text-sm text-app-muted">For {who}</p>
        </div>

        {mode !== 'push' ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="bulk-tags" className="text-sm text-app-muted">Tags, separated by commas</label>
            <input id="bulk-tags" value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="vip, press"
              className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            {parsed.invalid.length > 0 && (
              <p className="text-xs text-crimson">Not allowed: {parsed.invalid.join(', ')}. Use 1 to 30 letters, numbers, spaces, dashes or underscores.</p>
            )}
            {mode === 'add_tag' && <p className="text-xs text-app-muted">A person can have up to {MAX_TAGS_PER_PERSON} tags.</p>}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="bulk-title" className="text-sm text-app-muted">Title</label>
              <input id="bulk-title" value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="bulk-message" className="text-sm text-app-muted">Message</label>
              <textarea id="bulk-message" rows={3} value={message} maxLength={500} onChange={(e) => setMessage(e.target.value)}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <p className="text-xs text-app-muted">It goes to the phones of these {who} only, and shows in Push history as "Chosen people".</p>
          </div>
        )}

        {error && <p role="alert" className="text-sm text-crimson">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button>
          <Button onClick={() => void run()} disabled={working || (mode === 'push' ? !pushReady : !tagReady)}>
            {working ? <Spinner size="sm" className="text-ink" /> : mode === 'push' ? `Send to ${who}` : mode === 'add_tag' ? 'Add' : 'Remove'}
          </Button>
        </div>
      </div>
    </div>
  );
}
